import React, { useCallback, useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
//common components
import AppConstants from "../../constants";
import CustomDialog from "../../common/Dialog";
import TextBox from "../../common/TextBox";
import CustomSnackbar from "../../common/CustomSnackbar";

//assets
import attachIcon from "../../assets/icons/attach-key.svg";
import trashIcon from "../../assets/icons/trash.svg";
import downloadIcon from "../../assets/icons/download.svg";
import closeIcon from "../../assets/icons/circled-cancel.svg";

//Material UI Components
import { Grid, Typography, Button } from "@material-ui/core";

//styles
import { useStyles } from "./codUploadReceipt.styles";

import { userLogout } from "../../Login/redux/loginSlice";
import { dateWithReqFormat } from "../../utils/dateUtils";
import {
  FileStorageUpload,
  FileStorageSave,
} from "../../common/FileStorageOprations";
import { saveAs } from "file-saver";
import { act } from "react-dom/test-utils";

interface BaseUploadReceiptProps {
  open: boolean;
  hub: any;
  date: any;
  closePopup: () => void;
  reduxStates: {
    draftReceiptData: any;
    isFileUploaded: boolean;
    receiptDataAfterUpload: any;
    uploadError: string;
    uploadErrorCode: string;
    receiptsSubmittedFlag: boolean;
    receiptDataAfterSubmit: any;
    isErrorOnUpload: boolean;
  };
  actions: {
    uploadReceipt: (data: any) => void;
    submitReceipts: (data: any) => void;
    uploadClick: () => void;
    deletePrevUploadedReceipt: (data: any) => void;
  };
  transforms: {
    createPayloadToDeleteUploadedFile: (
      item: any,
      id: any,
      isTemp: boolean
    ) => any;
  };
}

const BaseUploadReceipt = (props: BaseUploadReceiptProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    open,
    hub,
    date,
    closePopup,
    reduxStates,
    actions,
    transforms,
  } = props;

  const {
    draftReceiptData,
    isFileUploaded,
    receiptDataAfterUpload,
    uploadError,
    uploadErrorCode,
    receiptsSubmittedFlag,
    receiptDataAfterSubmit,
    isErrorOnUpload,
  } = reduxStates;

  const {
    uploadReceipt,
    submitReceipts,
    uploadClick,
    deletePrevUploadedReceipt,
  } = actions;
  const { createPayloadToDeleteUploadedFile } = transforms;
  const dialogPaperProps = {
    classes: {
      root: classes.dialogPaperPropsRoot,
    },
  };
  const [receiptNo, setReceiptNo] = useState("");
  const [file, setFile] = useState({
    name: "",
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const snackbarType = useRef(AppConstants.SNACKBAR.TYPES.SUCCESS);
  const snackbarMessage = useRef("");
  const [tempReceipts, setTempReceipts] = useState([]);
  const [permanentReceipts, setPermanentReceipts] = useState([]);
  const [draftId, setDraftId] = useState("");
  const [enableAttachBtn, setEnableAttachBtn] = useState(false);
  const [maxSize, setMaxSize] = useState(false);
  const maxSizeSnackbarType = useRef(AppConstants.SNACKBAR.TYPES.ERROR);
  const maxSizeMessage = AppConstants.COD_RECON_CONSTANTS.MAX_FILE_SIZE;
  const handleDialogClose = () => {
    closePopup();
    setReceiptNo("");
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
    setMaxSize(false);
  };

  const handleSnackbarExited = useCallback(() => {
    if (
      uploadErrorCode ===
      AppConstants.COD_RECON_CONSTANTS.RESPONSE_CONSTANTS.ERROR_CODES
        .SESSION_TIMEOUT
    ) {
      dispatch(userLogout());
    }
  }, [dispatch, uploadErrorCode]);

  const handleReceiptTextboxChange = (event: any) => {
    const receiptNumber = event.target.value;
    setReceiptNo(receiptNumber);
    if (event.target.value.length > 0) {
      setEnableAttachBtn(true);
    } else {
      setEnableAttachBtn(false);
    }
  };

  function handleUpload(selectedFile: any) {
    setFile(selectedFile);
    dispatch(uploadClick());
    const data = new FormData();
    if (
      selectedFile.size > AppConstants.COD_RECON_CONSTANTS.MAX_FILE_SIZE_ALLOWED
    ) {
      setMaxSize(true);
      setEnableAttachBtn(false);
    } else {
      data.append("images", selectedFile);
      const request = JSON.stringify({
        id: draftReceiptData && draftReceiptData.id,
        receiptNumber: receiptNo,
      });
      data.append("request", request);
      dispatch(
        uploadReceipt({
          params: data,
        })
      );
    }
    setReceiptNo("");
  }

  const deleteUploadedFile = (item: any, id: any, isTemp: boolean) => {
    const payload = createPayloadToDeleteUploadedFile(item, id, isTemp);
    dispatch(
      deletePrevUploadedReceipt({
        params: payload,
      })
    );
  };

  const downloadFile = (item: any) => {
    if (item) {
      saveAs(item.url, item.name);
    }
  };

  const handleSubmitReceipt = useCallback(
    (draftId: any) => {
      const payload = { id: draftId };
      dispatch(
        submitReceipts({
          params: payload,
        })
      );
      setReceiptNo("");
    },
    [dispatch, submitReceipts]
  );

  const getUploadedFiles = () => {
    return permanentReceipts?.length
      ? permanentReceipts.map((item: any, index: number) => (
          <Grid
            key={`${item.name.en} - ${index}`}
            className="uploadedFilePanel"
            item
          >
            <Grid item className="container">
              <Grid item className="item">
                <Grid container className="content">
                  <Grid item>
                    <Typography className="fileName">{item.name}</Typography>
                    <Typography className="fileTimestamp">
                      Uploaded on{" "}
                      {dateWithReqFormat(
                        item.createdAt,
                        AppConstants.DATE_FORMAT
                      )}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <FileStorageSave icon={downloadIcon} item={item} />
                    <img
                      src={trashIcon}
                      alt="delete"
                      className="icon delete"
                      onClick={() => deleteUploadedFile(item, draftId, false)}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          deleteUploadedFile(item, draftId, false);
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ))
      : null;
  };

  const getPreviousUploads = () => {
    return (
      <>
        {permanentReceipts?.length ? (
          <div>
            <Typography className="panelTitle">Previous Uploads</Typography>
            {getUploadedFiles()}
          </div>
        ) : (
          <Grid container className="defaultPanel">
            <Grid item className="title">
              <Typography className="content">
                No previous uploads available
              </Typography>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  const renderTempReceiptsContainer = () => {
    return tempReceipts?.length
      ? tempReceipts.map((item: any, index: number) => (
          <Grid
            key={`${item.name.en} - ${index}`}
            className={classes.tempContainer}
            item
          >
            <Grid container className="content">
              <Grid item xs={6}>
                <TextBox
                  fullWidth
                  disabled
                  variant="outlined"
                  value={`Receipt Number - ${item.receiptNumber}`}
                  textBoxId="receiptNo"
                  name="receiptNo"
                  type="text"
                ></TextBox>
              </Grid>
              <Grid item xs={6} className="imgItem">
                <Typography className="name">{item.name}</Typography>
                <img
                  src={closeIcon}
                  alt="cancel"
                  className="icon"
                  onClick={() => deleteUploadedFile(item, draftId, true)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      deleteUploadedFile(item, draftId, true);
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        ))
      : null;
  };

  const renderReceiptConfig = () => {
    return (
      <Grid container>
        <Grid className={classes.contentItem} item xs={6}>
          <Grid className="itemContainer" container>
            <Grid className="itemLabel" item>
              <Typography>Receipt No</Typography>
            </Grid>
            <Grid className="itemInput" item>
              <TextBox
                fullWidth
                variant="outlined"
                value={receiptNo}
                textBoxId="receiptNo"
                name="receiptNo"
                onChange={handleReceiptTextboxChange}
                type="text"
                placeholderText="Receipt Number"
              ></TextBox>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6} className={classes.attachItem}>
          <>
            <Button
              disabled={!enableAttachBtn}
              className="secondary"
              variant="contained"
            >
              <FileStorageUpload
                accept=".jpg, .png, .jpeg, .csv, .xlsx, .xls, .pdf, .doc, .docx"
                onChange={handleUpload}
                icon={attachIcon}
                Text={"Attach Receipt"}
              />
            </Button>
          </>
        </Grid>
      </Grid>
    );
  };

  const getTitleContent = () => {
    return (
      <Typography className={classes.uploadDialogTitle}>
        Cash Receipts
      </Typography>
    );
  };

  const getDetailsContent = () => {
    return (
      <div className={classes.container}>
        <Grid container spacing={2}>
          <Grid item>
            <Typography>Store Name : {hub}</Typography>
          </Grid>
          <Grid item>
            <Typography>Date : {date.startDate}</Typography>
          </Grid>
        </Grid>
        <Grid container>
          <Grid container>
            <Grid item xs={6} className="currentContainer">
              {renderReceiptConfig()}
              {renderTempReceiptsContainer()}
            </Grid>
            <Grid container xs={6} className="prevContainer">
              <Grid container className="receiptsPanel">
                {getPreviousUploads()}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  };

  const getUploadActions = () => {
    return (
      <Grid className={classes.uploadButtonsContainer} container>
        <Button
          className="dialogBtn secondary"
          variant="contained"
          onClick={() => handleDialogClose()}
        >
          {AppConstants.BUTTONS.CANCEL}
        </Button>
        <Button
          className="dialogBtn primary"
          variant="contained"
          onClick={() => handleSubmitReceipt(draftId)}
        >
          {AppConstants.BUTTONS.PROCEED}
        </Button>
      </Grid>
    );
  };

  const openSnackbarPopup = (msg: string, type: string) => {
    snackbarMessage.current = msg;
    snackbarType.current = type;
    setOpenSnackbar(true);
  };

  useEffect(() => {
    if (receiptsSubmittedFlag) {
      setTempReceipts(receiptDataAfterSubmit?.tempReceiptImages);
      setPermanentReceipts(receiptDataAfterSubmit?.images);
      setEnableAttachBtn(false);
    }
  }, [receiptsSubmittedFlag, receiptDataAfterSubmit]);

  useEffect(() => {
    if (isFileUploaded) {
      setTempReceipts(receiptDataAfterUpload?.tempReceiptImages);
      setPermanentReceipts(receiptDataAfterUpload?.images);
      setEnableAttachBtn(false);
    }
  }, [isFileUploaded, receiptDataAfterUpload]);

  useEffect(() => {
    setDraftId(draftReceiptData?.id);
    setTempReceipts(draftReceiptData?.tempReceiptImages);
    setPermanentReceipts(draftReceiptData?.images);
    setEnableAttachBtn(false);
  }, [draftReceiptData]);

  useEffect(() => {
    if (isErrorOnUpload) {
      openSnackbarPopup(uploadError, AppConstants.SNACKBAR.TYPES.ERROR);
      setReceiptNo("");
    }
  }, [isErrorOnUpload, uploadError]);

  return (
    <div>
      <CustomDialog
        open={open}
        PaperProps={dialogPaperProps}
        title={getTitleContent()}
        content={getDetailsContent()}
        actions={getUploadActions()}
        handleClose={handleDialogClose}
      ></CustomDialog>
      <CustomSnackbar
        open={openSnackbar}
        handleClose={handleSnackbarClose}
        onExited={handleSnackbarExited}
        autoHideDuration={AppConstants.SNACKBAR.AUTO_HIDE_TIMEOUT}
        message={snackbarMessage.current}
        type={snackbarType.current}
      />
      <CustomSnackbar
        open={maxSize}
        handleClose={handleSnackbarClose}
        onExited={handleSnackbarExited}
        autoHideDuration={AppConstants.SNACKBAR.AUTO_HIDE_TIMEOUT}
        message={maxSizeMessage}
        type={maxSizeSnackbarType.current}
      />
    </div>
  );
};

export default BaseUploadReceipt;
